import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, CheckCircle2, Circle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Searchability score for the member's public micro-site — motivates
// completing the SEO/GEO details. Refreshes when `refreshKey` changes
// (bumped after the SEO assistant saves).
export default function SeoScoreBadge({ token, refreshKey }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/seo/score/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch {
        /* badge is optional — hide on error */
      }
    })();
  }, [token, refreshKey]);

  if (!data) return null;
  const pct = Math.round((data.score / data.max) * 100);
  const tone = pct >= 70
    ? { text: 'text-emerald-400', bar: 'bg-emerald-500', label: 'Great — easy to find' }
    : pct >= 40
      ? { text: 'text-amber-400', bar: 'bg-amber-500', label: 'Getting there' }
      : { text: 'text-red-400', bar: 'bg-red-500', label: 'Hard to find right now' };

  return (
    <div className="rounded-2xl bg-brand-surface border border-white/5 p-6 space-y-4" data-testid="seo-score-badge">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Search className="w-4 h-4 text-brand-gold" /> Searchability score
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            How findable your micro-site is on Google and AI search — complete the items below to raise it.
          </p>
        </div>
        <div className="text-right">
          <p className={`text-3xl font-bold ${tone.text}`} data-testid="seo-score-value">{data.score}<span className="text-sm text-gray-500">/{data.max}</span></p>
          <p className={`text-[11px] ${tone.text}`}>{tone.label}</p>
        </div>
      </div>

      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full rounded-full ${tone.bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>

      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5" data-testid="seo-score-checks">
        {data.checks.map((c) => (
          <div key={c.id} className="flex items-center gap-2 text-xs">
            {c.done ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            ) : (
              <Circle className="w-3.5 h-3.5 text-gray-600 shrink-0" />
            )}
            <span className={c.done ? 'text-gray-300' : 'text-gray-500'}>{c.label}</span>
            <span className="ml-auto text-[10px] text-gray-600">+{c.max}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
